// deps
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { isEmpty } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

import { Toast } from "@swiggy-private/dls-web-core/dist/components/Toast";

import MultiSelectContent from "./MultiSelectContent";
import SingleSelectContent from "./SingleSelectContent";
import SortContent from "./SortContent";

import { Container, ContentFooter, Header, StyledButton, StyledTextButton } from "./styles";

import { SORT_ID_FILTERS, SORT_KEY_VERTICAL_NAVIGATION, TEXTS, TOAST_MESSAGE } from "../constants";
import { getAllSelectedFilter } from "../helpers";
import { IFacetList, IHalfCardExtraParams, IHalfCardContent } from "../types";
import { FILTER_TYPE } from "../types";
import { VerticalNavigation } from "../VerticalNavigation";

function HalfCardContent({ sortList, filterList, onApply, onClearFilters }: IHalfCardContent) {
    const [selectedItem, setSelectedItem] = useState(
        isEmpty(sortList) ? filterList?.[0]?.id : SORT_KEY_VERTICAL_NAVIGATION
    );
    const [showToast, setShowToast] = useState(false);
    const preSelectedFilters = useMemo(
        () => getAllSelectedFilter(filterList, sortList),
        [filterList, sortList]
    );
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
        ...preSelectedFilters,
    });
    const [showFooter, setShowFooter] = useState(!isEmpty(selectedFilters));

    const verticalNavigationItems = useMemo(() => {
        if (isEmpty(sortList)) {
            return filterList;
        }

        return [{ id: SORT_KEY_VERTICAL_NAVIGATION, label: TEXTS.SORT }, ...filterList];
    }, [filterList, sortList]);

    const buildExtraParams = useCallback((): IHalfCardExtraParams | undefined => {
        if (selectedItem === SORT_KEY_VERTICAL_NAVIGATION) return undefined;
        const currentFacet = (filterList || []).find((f) => f.id === selectedItem);
        const selectedIds = selectedFilters[selectedItem] || [];
        const selectedFacetArray = currentFacet?.facetInfo?.filter((fi) =>
            selectedIds.includes(fi.id)
        );
        return {
            selectedFacet: selectedFacetArray,
            facetInfo: currentFacet,
        };
    }, [filterList, selectedFilters, selectedItem]);

    const updateFilterList = useCallback(
        (value: string[], id: string) => {
            setSelectedFilters((prev) => {
                const updated: Record<string, string[]> = {
                    ...prev,
                    [id]: value,
                };
                if (value.length === 0) {
                    delete updated[id];
                }
                return updated;
            });
            setShowFooter(true);
        },
        [setSelectedFilters]
    );

    const AVAILABLE_CONTENTS: Record<string, JSX.Element> = useMemo(() => {
        const sortContent: Record<string, JSX.Element> = isEmpty(sortList)
            ? {}
            : {
                  sort: (
                      <SortContent
                          sortList={sortList}
                          selectedFilters={selectedFilters[SORT_ID_FILTERS] || []}
                          updateFilterList={updateFilterList}
                      />
                  ),
              };

        const listContent = filterList?.reduce(
            (acc, filter: IFacetList) => {
                switch (filter.selection) {
                    case FILTER_TYPE.SELECT_TYPE_MULTISELECT:
                        acc[filter.id] = (
                            <MultiSelectContent
                                key={filter.id}
                                filter={filter}
                                selectedFilters={selectedFilters[filter.id] || []}
                                updateFilterList={updateFilterList}
                            />
                        );
                        break;
                    case FILTER_TYPE.SELECT_TYPE_SINGLESELECT:
                        acc[filter.id] = (
                            <SingleSelectContent
                                key={filter.id}
                                filter={filter}
                                selectedFilters={selectedFilters[filter.id] || []}
                                updateFilterList={updateFilterList}
                            />
                        );
                        break;
                    default:
                        break;
                }
                return acc;
            },
            {} as Record<string, JSX.Element>
        );

        return { ...(isEmpty(sortList) ? {} : sortContent), ...listContent };
    }, [filterList, selectedFilters, sortList, updateFilterList]);

    const [content, setContent] = useState(
        AVAILABLE_CONTENTS[isEmpty(sortList) ? filterList?.[0]?.id : SORT_KEY_VERTICAL_NAVIGATION]
    );
    const clearFilters = useCallback(() => {
        setSelectedFilters({});
        setShowToast(true);
        onClearFilters?.();
    }, [onClearFilters]);

    useEffect(() => {
        setContent(AVAILABLE_CONTENTS[selectedItem]);
    }, [AVAILABLE_CONTENTS, selectedItem]);

    const hideToast = useCallback(() => {
        setShowToast(false);
    }, []);

    const handleApply = useCallback(() => {
        onApply(selectedFilters, buildExtraParams());
    }, [selectedFilters, buildExtraParams, onApply]);

    return (
        <div>
            <Header>
                <Text variant="V2.heading1.neutral.black" color="text_High_Emphasis">
                    {TEXTS.FILTER}
                </Text>
            </Header>
            <Container>
                <VerticalNavigation
                    content={
                        <Text color="text_High_Emphasis" variant="V2.body1.neutral.medium">
                            {content}
                        </Text>
                    }
                    selectedItemID={selectedItem}
                    onChangeSelectedItem={(newItemID) => {
                        setSelectedItem(newItemID);
                        setContent(AVAILABLE_CONTENTS[newItemID]);
                    }}
                    items={verticalNavigationItems}
                />
            </Container>
            <Toast
                show={showToast}
                autoDismiss
                onDismiss={hideToast}
                message={TOAST_MESSAGE.CLEAR_FILTERS}
            />

            {showFooter && (
                <ContentFooter>
                    <StyledTextButton
                        color="background_Button_Primary"
                        buttonSize="medium"
                        isDisabled={isEmpty(selectedFilters) ? true : false}
                        onClick={clearFilters}
                    >
                        {TEXTS.CLEAR_FILTERS}
                    </StyledTextButton>
                    <StyledButton onClick={handleApply} variant="primary">
                        <Text
                            variant="V2.subheading1.neutral.bold"
                            color="text_Button_Primary"
                            textAlign="center"
                        >
                            {TEXTS.APPLY}
                        </Text>
                    </StyledButton>
                </ContentFooter>
            )}
        </div>
    );
}

export default memo(HalfCardContent);
